import { dataURLToBlob, createObjectURL } from 'blob-util';

import { startConversation } from './conversation';
import { subscribeFaye } from './faye';
import http from './http';
import { updateUser, immediateUpdate as immediateUpdateUser } from './user';

import * as IntegrationActions from '../actionTypes/integrations';

const LINKING_CONFIRMATION_TYPE = {
    PROMPT: 'prompt',
    IMMEDIATE: 'immediate',
};

let fetchingWeChat = false;
let fetchingViber = false;
let fetchingWhatsApp = false;

export function setError(channel) {
    return {
        type: IntegrationActions.SET_ERROR,
        channel,
    };
}

export function unsetError(channel) {
    return {
        type: IntegrationActions.UNSET_ERROR,
        channel,
    };
}

export function setWeChatQRCode(code) {
    return {
        type: IntegrationActions.SET_WECHAT_QR_CODE,
        code,
    };
}

export function resetIntegrations() {
    return {
        type: IntegrationActions.RESET_INTEGRATIONS,
    };
}
export function setTwilioIntegrationState(attrs) {
    return {
        type: IntegrationActions.SET_TWILIO_INTEGRATION_STATE,
        attrs,
    };
}

export function resetTwilioIntegrationState() {
    return {
        type: IntegrationActions.RESET_TWILIO_INTEGRATION_STATE,
    };
}

export function setMessageBirdIntegrationState(attrs) {
    return {
        type: IntegrationActions.SET_MESSAGEBIRD_INTEGRATION_STATE,
        attrs,
    };
}

export function resetMessageBirdIntegrationState() {
    return {
        type: IntegrationActions.RESET_MESSAGEBIRD_INTEGRATION_STATE,
    };
}

export function setViberQRCode(code) {
    return {
        type: IntegrationActions.SET_VIBER_QR_CODE,
        code,
    };
}

export function setLinkRequest(channel, linkRequest) {
    return {
        type: IntegrationActions.SET_LINK_REQUEST,
        channel,
        linkRequest,
    };
}

export function resetLinkRequest(channel) {
    return {
        type: IntegrationActions.RESET_LINK_REQUEST,
        channel,
    };
}

export function setWhatsAppCodeWhileLoading() {
    return {
        type: IntegrationActions.SET_WHATSAPP_CODE_WHILE_LOADING,
    };
}

function ensureUserAndFayeConnection(dispatch, getState) {
    if (getState().user._id) {
        return subscribeFaye();
    } else {
        return dispatch(startConversation('link:request')).then(() => dispatch(immediateUpdateUser()));
    }
}

export function fetchWeChatQRCode() {
    return (dispatch, getState) => {
        const {
            config: { appId, integrations },
            integrations: { wechat },
        } = getState();

        if (wechat.qrCode || fetchingWeChat) {
            return Promise.resolve();
        }

        const wechatIntegration = integrations.find(({ type }) => type === 'wechat');
        if (!wechatIntegration) {
            return Promise.resolve();
        }

        dispatch(unsetError('wechat'));
        fetchingWeChat = true;

        return ensureUserAndFayeConnection(dispatch, getState)
            .then(() => {
                const {
                    user: { _id },
                } = getState();

                return dispatch(
                    http('GET', `/v2/apps/${appId}/appusers/${_id}/integrations/${wechatIntegration._id}/qrcode`),
                );
            })
            .then(({ url }) => {
                dispatch(setWeChatQRCode(url));
            })
            .catch(() => {
                dispatch(setError('wechat'));
            })
            .then(() => {
                fetchingWeChat = false;
            });
    };
}

export function fetchViberQRCode() {
    return (dispatch, getState) => {
        const {
            config: { appId, integrations },
            integrations: { viber },
        } = getState();

        if (viber.qrCode || fetchingViber) {
            return Promise.resolve();
        }

        const viberIntegration = integrations.find(({ type }) => type === 'viber');
        if (!viberIntegration) {
            return Promise.resolve();
        }

        dispatch(unsetError('viber'));
        fetchingViber = true;

        return ensureUserAndFayeConnection(dispatch, getState)
            .then(() => {
                const {
                    user: { _id },
                } = getState();

                return dispatch(
                    http('GET', `/v2/apps/${appId}/appusers/${_id}/integrations/${viberIntegration._id}/qrcode`),
                );
            })
            .then(({ url }) => dataURLToBlob(url))
            .then((blob) => {
                const blobUrl = createObjectURL(blob);
                dispatch(setViberQRCode(blobUrl));
            })
            .catch(() => {
                dispatch(setError('viber'));
            })
            .then(() => {
                fetchingViber = false;
            });
    };
}

export function fetchWhatsAppLinkAndQRCode() {
    return (dispatch, getState) => {
        const {
            config: { integrations, appId },
            integrations: { whatsapp },
        } = getState();
        const integration = integrations.find((integration) => 'whatsapp' === integration.type);

        if (
            (whatsapp.linkRequest && whatsapp.linkRequest.qrCode && whatsapp.linkRequest.url) ||
            fetchingWhatsApp ||
            !integration
        ) {
            return Promise.resolve();
        }

        dispatch(unsetError('whatsapp'));
        fetchingWhatsApp = true;

        return ensureUserAndFayeConnection(dispatch, getState)
            .then(() => {
                const {
                    user: { _id },
                } = getState();

                return dispatch(
                    http('GET', `/v2/apps/${appId}/appusers/${_id}/linkrequest`, {
                        integrationIds: integration._id,
                    }),
                );
            })
            .then((res) => {
                const { qrCode } = res.linkRequests[0];
                const blob = dataURLToBlob(qrCode);
                const blobUrl = createObjectURL(blob);
                res.linkRequests[0].qrCode = blobUrl;
                dispatch(setLinkRequest('whatsapp', res.linkRequests[0]));
            })
            .catch(() => {
                dispatch(setError('whatsapp'));
            })
            .then(() => {
                fetchingWhatsApp = false;
            });
    };
}

const smsFunctionMap = {
    twilio: {
        updateSMSAttributes: updateTwilioAttributes,
        resetSMSAttributes: resetTwilioAttributes,
    },
    messagebird: {
        updateSMSAttributes: updateMessageBirdAttributes,
        resetSMSAttributes: resetMessageBirdAttributes,
    },
};

export function updateSMSAttributes(attr, type) {
    return smsFunctionMap[type].updateSMSAttributes(attr);
}

export function updateTwilioAttributes(attr) {
    return (dispatch) => {
        dispatch(setTwilioIntegrationState(attr));
    };
}

export function updateMessageBirdAttributes(attr) {
    return (dispatch) => {
        dispatch(setMessageBirdIntegrationState(attr));
    };
}

export function resetSMSAttributes(type) {
    return smsFunctionMap[type].resetSMSAttributes();
}

export function resetTwilioAttributes() {
    return (dispatch) => {
        dispatch(resetTwilioIntegrationState());
    };
}

export function resetMessageBirdAttributes() {
    return (dispatch) => {
        dispatch(resetMessageBirdIntegrationState());
    };
}

export function fetchTwilioAttributes() {
    return (dispatch, getState) => {
        const {
            user: { clients = [], pendingClients = [] },
        } = getState();
        const client = clients.find((client) => client.platform === 'twilio');
        const pendingClient = pendingClients.find((client) => client.platform === 'twilio');

        if (client) {
            dispatch(
                updateTwilioAttributes({
                    linkState: 'linked',
                    appUserNumber: client.displayName,
                }),
            );
        } else if (pendingClient) {
            dispatch(
                updateTwilioAttributes({
                    linkState: 'pending',
                    appUserNumber: pendingClient.displayName,
                }),
            );
        }
    };
}

export function fetchMessageBirdAttributes() {
    return (dispatch, getState) => {
        const {
            user: { clients = [], pendingClients = [] },
        } = getState();
        const client = clients.find((client) => client.platform === 'messagebird');
        const pendingClient = pendingClients.find((client) => client.platform === 'messagebird');

        if (client) {
            dispatch(
                updateMessageBirdAttributes({
                    linkState: 'linked',
                    appUserNumber: client.displayName,
                }),
            );
        } else if (pendingClient) {
            dispatch(
                updateMessageBirdAttributes({
                    linkState: 'pending',
                    appUserNumber: pendingClient.displayName,
                }),
            );
        }
    };
}

export function linkChannel(criteria, confirmationType) {
    return (dispatch, getState) => {
        const {
            config: { appId },
            user: { pendingClients = [] },
        } = getState();

        return ensureUserAndFayeConnection(dispatch, getState)
            .then(() => {
                const {
                    user: { _id: appUserId },
                    appState: { activeConversationId: conversationId },
                } = getState();

                return dispatch(
                    http('POST', `/v2/apps/${appId}/appusers/${appUserId}/clients`, {
                        criteria,
                        confirmation: {
                            type: confirmationType,
                        },
                        target: {
                            conversationId,
                        },
                    }),
                );
            })
            .then(({ client }) => {
                dispatch(
                    updateUser({
                        pendingClients: [...pendingClients, client],
                    }),
                );

                return client;
            });
    };
}

export function unlinkChannel(type) {
    return (dispatch, getState) => {
        const {
            config: { appId },
            user: { _id, clients, pendingClients },
        } = getState();
        const client =
            clients.find((client) => client.platform === type) ||
            pendingClients.find((client) => client.platform === type);

        if (!client) {
            return Promise.resolve();
        }

        return dispatch(http('DELETE', `/v2/apps/${appId}/appusers/${_id}/clients/${client.id}`)).then(() => {
            dispatch(
                updateUser({
                    pendingClients: pendingClients.filter((pendingClient) => pendingClient.id !== client.id),
                    clients: clients.filter((currentClient) => currentClient.id !== client.id),
                }),
            );
        });
    };
}

export function failChannelLink(platform) {
    return (dispatch) => {
        return dispatch(unlinkChannel(platform)).then(() => {
            dispatch(setError(platform));
        });
    };
}

export function linkSMSChannel(criteria) {
    return (dispatch) => {
        return dispatch(linkChannel(criteria, LINKING_CONFIRMATION_TYPE.PROMPT));
    };
}

export function unlinkSMSChannel(type) {
    return (dispatch, getState) => {
        return dispatch(unlinkChannel(type))
            .then(() => {
                dispatch(
                    updateSMSAttributes(
                        {
                            linkState: 'unlinked',
                            appUserNumber: '',
                            appUserNumberValid: false,
                        },
                        type,
                    ),
                );
            })
            .catch((e) => {
                const {
                    response: { status },
                } = e;
                const {
                    ui: {
                        text: { smsBadRequestError },
                    },
                } = getState();
                // Deleting a client that was never linked
                if (status === 400) {
                    dispatch(
                        updateSMSAttributes(
                            {
                                linkState: 'unlinked',
                            },
                            type,
                        ),
                    );
                } else {
                    dispatch(
                        updateSMSAttributes(
                            {
                                linkState: 'unlinked',
                                hasError: true,
                                errorMessage: smsBadRequestError,
                            },
                            type,
                        ),
                    );
                }
            });
    };
}

export function pingSMSChannel(type) {
    return (dispatch, getState) => {
        const {
            config: { appId },
            user: { _id, clients },
        } = getState();
        const client = clients.find((client) => client.platform === type);

        return dispatch(http('POST', `/v2/apps/${appId}/appusers/${_id}/clients/${client.id}/ping`))
            .then(() => {
                dispatch(
                    updateSMSAttributes(
                        {
                            linkState: 'linked',
                        },
                        type,
                    ),
                );
            })
            .catch(() => {
                const {
                    ui: {
                        text: { smsPingChannelError },
                    },
                } = getState();
                dispatch(
                    updateSMSAttributes(
                        {
                            hasError: true,
                            errorMessage: smsPingChannelError,
                        },
                        type,
                    ),
                );
            });
    };
}

export function cancelSMSLink(client) {
    return (dispatch, getState) => {
        const {
            user: { pendingClients },
            integrations,
            ui: {
                text: { smsLinkCancelled },
            },
        } = getState();
        const { appUserNumber } = integrations[client.platform];

        dispatch(
            updateUser({
                pendingClients: pendingClients.filter((pendingClient) => pendingClient.id !== client.id),
            }),
        );

        dispatch(
            updateSMSAttributes(
                {
                    linkState: 'unlinked',
                    hasError: true,
                    errorMessage: smsLinkCancelled.replace('{appUserNumber}', appUserNumber),
                },
                client.platform,
            ),
        );
    };
}

export function matchSMSLink(client) {
    return (dispatch, getState) => {
        const {
            user: { pendingClients },
        } = getState();

        dispatch(
            updateUser({
                pendingClients: [...pendingClients.filter((pendingClient) => pendingClient.id !== client.id), client],
            }),
        );

        dispatch(
            updateSMSAttributes(
                {
                    linkState: 'pending',
                    isWorking: false,
                },
                client.platform,
            ),
        );
    };
}

export function failSMSLink(error, client) {
    return (dispatch, getState) => {
        const {
            user: { pendingClients },
            ui: {
                text: {
                    smsTooManyRequestsError,
                    smsTooManyRequestsOneMinuteError,
                    smsBadRequestError,
                    smsUnhandledError,
                },
            },
        } = getState();
        const retryAfter = error.headers ? error.headers.get('retry-after') : error.retryAfter;
        const { status } = error;
        let errorMessage;

        if (status === 429) {
            const minutes = Math.ceil(retryAfter / 60);
            if (minutes > 1) {
                errorMessage = smsTooManyRequestsError.replace('{minutes}', minutes);
            } else {
                errorMessage = smsTooManyRequestsOneMinuteError;
            }
        } else if (status > 499) {
            errorMessage = smsUnhandledError;
        } else {
            errorMessage = smsBadRequestError;
        }

        dispatch(
            updateUser({
                pendingClients: pendingClients.filter((pendingClient) => pendingClient.id !== client.id),
            }),
        );

        dispatch(
            updateSMSAttributes(
                {
                    hasError: true,
                    isWorking: false,
                    linkState: 'unlinked',
                    errorMessage: errorMessage,
                },
                client.platform,
            ),
        );
    };
}

export function linkEmailChannel(criteria) {
    return (dispatch) => {
        return dispatch(linkChannel(criteria, LINKING_CONFIRMATION_TYPE.IMMEDIATE));
    };
}

export function fetchLinkRequest(type) {
    return (dispatch, getState) => {
        const {
            config: { integrations, appId },
        } = getState();
        const integration = integrations.find((integration) => type === integration.type);

        if (!integration) {
            return Promise.resolve();
        }

        dispatch(unsetError(type));

        return ensureUserAndFayeConnection(dispatch, getState)
            .then(() => {
                const {
                    user: { _id },
                } = getState();

                return dispatch(
                    http('GET', `/v2/apps/${appId}/appusers/${_id}/linkrequest`, {
                        integrationIds: integration._id,
                    }),
                );
            })
            .then((res) => {
                dispatch(setLinkRequest(type, res.linkRequests[0]));
            })
            .catch(() => {
                dispatch(setError(type));
            });
    };
}
